import { AnswerWizard } from '../questions/answer-wizard';
import { wizardCounts, type WizardValue } from '../questions/answer-wizard-model';
import type { TranscriptItem } from './coding-events';
import { answerSendGate, panelAllowsSkip } from './answer-panel';

type QuestionsItem = Extract<TranscriptItem, { kind: 'questions' }>;

/**
 * The Builder's answer panel (FRONT-3155): the open question batch, answered
 * one question at a time, as a blue band at the top of the reply box. The
 * transcript card is a read-only record of the same questions; the one Send
 * button of the composer submits everything chosen here, with whatever is
 * typed riding along as the person's note.
 *
 * This is only the Builder's wiring around the surface-agnostic wizard — which
 * questions allow Skip, and why Send is off when it is (FRONT-1670: a disabled
 * Send always says why, in visible text). Nothing chosen and nothing typed is
 * said by the reply box's placeholder (`answerPlaceholder`); a split-off batch
 * while Eddy works gets a line in the band. Nothing else is explained in words
 * (eddy, 2026-10-26: "if you need text to explain, make it easier").
 */
export function CodingAnswerPanel({
  item,
  value,
  onChange,
  typedText,
  detachedAndBusy,
  onDone,
}: {
  item: QuestionsItem;
  value: WizardValue;
  onChange: (next: WizardValue) => void;
  /** What is typed in the reply box right now. */
  typedText: string;
  /** A split-off batch while the run is still working — see `answerSendGate`. */
  detachedAndBusy: boolean;
  /** Next on the last question: move focus to Send. */
  onDone: () => void;
}) {
  const gate = answerSendGate({ counts: wizardCounts(value), typedText, detachedAndBusy });
  return (
    <div data-testid="coding-answer-panel">
      <AnswerWizard
        questions={item.questions}
        value={value}
        onChange={onChange}
        allowSkip={panelAllowsSkip(item)}
        // The free-text "Other" is the reply box itself, never a row per question.
        allowOther={false}
        idBase={item.batchId}
        onDone={onDone}
        // The empty case ("nothing chosen, nothing typed") is said by the reply
        // box's own placeholder, right where the person looks; only the reason
        // the placeholder cannot carry gets a line here.
        footer={
          gate.reason && detachedAndBusy ? <span data-testid="coding-answer-send-reason">{gate.reason}</span> : undefined
        }
      />
    </div>
  );
}
